import { create } from 'zustand';
import {
  businessHoursService,
  BusinessHoursSchedule,
  BusinessHoursUsage,
  CreateBusinessHoursDto,
  UpdateBusinessHoursDto,
} from '@/services/businessHours.service';

export type { CreateBusinessHoursDto, UpdateBusinessHoursDto, BusinessHoursUsage };

interface BusinessHoursState {
  schedules: BusinessHoursSchedule[];
  isLoading: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  error: string | null;

  fetchSchedules: () => Promise<void>;
  createSchedule: (dto: CreateBusinessHoursDto) => Promise<BusinessHoursSchedule | null>;
  updateSchedule: (
    id: string,
    dto: UpdateBusinessHoursDto,
  ) => Promise<BusinessHoursSchedule | null>;
  fetchUsage: (id: string) => Promise<BusinessHoursUsage | null>;
  deleteSchedule: (id: string) => Promise<boolean>;
  setAsDefault: (id: string) => Promise<boolean>;
  toggleActive: (id: string) => Promise<boolean>;
  reset: () => void;
}

export const useBusinessHoursStore = create<BusinessHoursState>()((set, get) => ({
  schedules: [],
  isLoading: false,
  isSaving: false,
  isDeleting: false,
  error: null,

  fetchSchedules: async () => {
    set({ isLoading: true, error: null });
    try {
      const schedules = await businessHoursService.getSchedules();
      set({ schedules, isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch business hours',
        isLoading: false,
      });
    }
  },

  createSchedule: async (dto) => {
    set({ isSaving: true, error: null });
    try {
      const created = await businessHoursService.createSchedule(dto);

      // Only one schedule can be default at a time.
      let schedules = get().schedules;
      if (dto.isDefault) {
        schedules = schedules.map((s) => ({ ...s, isDefault: false }));
      }

      set({ schedules: [...schedules, created], isSaving: false });
      return created;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create business hours',
        isSaving: false,
      });
      return null;
    }
  },

  updateSchedule: async (id, dto) => {
    set({ isSaving: true, error: null });
    try {
      const updated = await businessHoursService.updateSchedule(id, dto);

      let schedules = get().schedules;
      if (dto.isDefault) {
        schedules = schedules.map((s) =>
          s.id === id ? updated : { ...s, isDefault: false },
        );
      } else {
        schedules = schedules.map((s) => (s.id === id ? updated : s));
      }

      set({ schedules, isSaving: false });
      return updated;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update business hours',
        isSaving: false,
      });
      return null;
    }
  },

  /**
   * What's using a schedule, read when the delete dialog opens.
   *
   * Returns null instead of setting `error` when the lookup fails: this is an
   * advisory check, and surfacing it in the page-level error banner would read as
   * "the page is broken" when the only thing lost is the warning. The dialog
   * degrades to its plain confirmation instead.
   */
  fetchUsage: async (id) => {
    try {
      return await businessHoursService.getUsage(id);
    } catch {
      return null;
    }
  },

  deleteSchedule: async (id) => {
    set({ isDeleting: true, error: null });
    try {
      await businessHoursService.deleteSchedule(id);
      set({
        schedules: get().schedules.filter((s) => s.id !== id),
        isDeleting: false,
      });
      return true;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete business hours',
        isDeleting: false,
      });
      return false;
    }
  },

  setAsDefault: async (id) => {
    return (await get().updateSchedule(id, { isDefault: true })) !== null;
  },

  toggleActive: async (id) => {
    const schedule = get().schedules.find((s) => s.id === id);
    if (!schedule) return false;
    return (await get().updateSchedule(id, { isActive: !schedule.isActive })) !== null;
  },

  reset: () => {
    set({
      schedules: [],
      isLoading: false,
      isSaving: false,
      isDeleting: false,
      error: null,
    });
  },
}));
